import * as ToggleGroup from "@radix-ui/react-toggle-group";

// The dashboard's shared page parts, first drawn for the GrowthOS Alloy page: the navy hero card, the white panel
// and its title line, the three-figure stat row, the share bar with its legend, the segmented control, the state
// chip and the icon square. A page that uses these reads as a sibling of Alloy. They know nothing about credits
// or agents: a caller hands them words, numbers and class names.

export function Panel({ className = "", children, ...props }) {
  return (
    <section className={`rounded-2xl border border-sm-ink/10 bg-white p-5 md:p-[22px] ${className}`} {...props}>
      {children}
    </section>
  );
}

/** A panel's title with a quiet note or control at the right. */
export function PanelTitle({ children, aside = null, id }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <h2 id={id} className="text-[15px] font-bold leading-tight tracking-[-0.01em] text-sm-ink">{children}</h2>
      {aside}
    </div>
  );
}

// A figure that is not in a good state says so in its own colour, with a dot in front of it.
const STAT_TONES = Object.freeze({
  warn: { value: "text-[#B45309]", dot: "bg-[#F59E0B]" },
  bad: { value: "text-[#B91C1C]", dot: "bg-[#EF4343]" },
});

/**
 * Up to three figures side by side with hairlines between them: a tiny uppercase label over a bold value.
 * A stat may add a quiet line under its value (`sub`), something to press (`action`), and a `tone`
 * ("warn", "bad") that colours the value when the thing it counts needs attention.
 */
export function StatGrid({ stats, className = "", ...props }) {
  return (
    <dl className={`grid grid-cols-3 ${className}`} {...props}>
      {stats.map((stat, index) => {
        const tone = STAT_TONES[stat.tone];
        return (
          <div key={stat.label} data-testid={stat.testId} className={`flex min-w-0 flex-col justify-between gap-1.5 ${index > 0 ? "border-l border-sm-ink/10 pl-3 sm:pl-4 md:pl-5" : ""}`}>
            <dt className="text-[10px] font-extrabold uppercase leading-snug tracking-[0.08em] text-sm-muted sm:text-[11px] sm:tracking-[0.14em]">{stat.label}</dt>
            <dd className={`text-base font-bold leading-tight tabular-nums sm:text-lg ${tone ? tone.value : "text-sm-ink"}`}>
              {tone ? <span className={`mb-[3px] mr-1.5 inline-block h-2 w-2 rounded-full align-middle ${tone.dot}`} aria-hidden="true" /> : null}
              {stat.value}
            </dd>
            {stat.sub ? <dd className={`text-xs leading-snug ${tone ? `${tone.value} opacity-90` : "text-sm-ink/65"}`}>{stat.sub}</dd> : null}
            {stat.action ? <dd className="pt-0.5">{stat.action}</dd> : null}
          </div>
        );
      })}
    </dl>
  );
}

/**
 * How a whole splits into named parts: a bar, its legend, and a quiet note at the right.
 * parts: [{ key, percent, barClass, legend }]. Parts at 0% stay in the legend and leave the bar.
 */
export function ShareBar({ parts, note = null, label, testId }) {
  return (
    <>
      <div data-testid={testId} className="mt-4 flex h-3 overflow-hidden rounded-full bg-sm-ink/[0.08]" role="img" aria-label={label ?? parts.map((part) => part.legend).join(", ")}>
        {parts.filter((part) => part.percent > 0).map((part) => (
          <span key={part.key} className={`h-full ${part.barClass}`} style={{ width: `${part.percent}%` }} />
        ))}
      </div>
      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 text-xs">
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-sm-ink/75">
          {parts.map((part) => (
            <span key={part.key} className="inline-flex items-center gap-1.5 tabular-nums">
              <span className={`h-2 w-2 rounded-sm ${part.barClass}`} aria-hidden="true" />
              {part.legend}
            </span>
          ))}
        </div>
        {note ? <span className="text-sm-ink/65">{note}</span> : null}
      </div>
    </>
  );
}

/**
 * One choice out of a few, as a row of segments (reasoning effort, a skill group). Pressing the chosen segment
 * again keeps it chosen, and nothing changes while `busy`.
 * options: [{ value, label, testId }].
 */
export function Segmented({ value, onChange, options, label, busy = false, testId }) {
  return (
    <ToggleGroup.Root
      type="single"
      value={value}
      onValueChange={(next) => { if (next && next !== value && !busy) onChange(next); }}
      aria-label={label}
      aria-busy={busy || undefined}
      data-testid={testId}
      className={`grid auto-cols-fr grid-flow-col gap-0.5 rounded-[10px] bg-sm-ink/5 p-[3px] ${busy ? "opacity-70" : ""}`}
    >
      {options.map((option) => (
        <ToggleGroup.Item
          key={option.value}
          value={option.value}
          data-testid={option.testId}
          className="flex h-[34px] min-w-0 items-center justify-center whitespace-nowrap rounded-lg px-0.5 text-[11.5px] font-medium text-sm-ink/60 transition hover:text-sm-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sm-accent data-[state=on]:bg-white data-[state=on]:font-bold data-[state=on]:text-sm-ink data-[state=on]:shadow-[0_1px_2px_rgba(15,36,66,0.12),0_0_0_1px_rgba(15,36,66,0.06)] sm:px-1 sm:text-[13px]"
        >
          {option.label}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
}

/** The navy ground of a hero card, also the header of a popup that belongs to one. */
export const HERO_BACKGROUND = "radial-gradient(360px 220px at 90% 0%, rgba(255,255,255,0.12), transparent 70%), linear-gradient(135deg, #0F2442 0%, #003B8D 58%, #2F6DB5 100%)";

/**
 * The navy card a page leads with: brand line and pill at the top, the main figure, a footer row. It keeps one
 * height on a desktop; `grow` lets it get taller when its words can run long (a name, not a number).
 */
export function HeroCard({ grow = false, className = "", children, ...props }) {
  return (
    <div
      className={`relative flex min-h-[250px] flex-col justify-between gap-6 overflow-hidden rounded-[20px] p-6 text-white ${grow ? "md:min-h-[272px]" : "md:h-[272px]"} md:p-[26px] ${className}`}
      style={{ background: HERO_BACKGROUND, boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.14), 0 18px 40px -20px rgba(15,36,66,0.55)" }}
      {...props}
    >
      {children}
    </div>
  );
}

/** "GrowthOS Alloy", "GrowthOS Agent": the product's name on a hero card. */
export function HeroWordmark({ product }) {
  return (
    <div className="font-display text-lg font-extrabold leading-none tracking-[-0.025em]">
      <span className="text-white">Growth</span><span className="text-[#9cc6f0]">OS</span>
      <span className="ml-1.5 font-bold text-white/90">{product}</span>
    </div>
  );
}

const HERO_PILL_TONES = Object.freeze({
  on: "bg-[#22C55E]/20 text-[#86efac]",
  busy: "bg-white/15 text-white",
  quiet: "bg-white/15 text-white/80",
  warn: "bg-[#F59E0B]/25 text-[#fcd34d]",
  bad: "bg-[#EF4343]/25 text-[#fecaca]",
});

/** The small capital word at the top right of a hero card (ON, OFF, READY). `dot` lights a green LED. */
export function HeroPill({ tone = "quiet", dot = false, children, testId }) {
  return (
    <span data-testid={testId} className={`inline-flex h-[22px] items-center gap-1.5 rounded-full px-2.5 text-[11px] font-extrabold tracking-[0.12em] ${HERO_PILL_TONES[tone] || HERO_PILL_TONES.quiet}`}>
      {dot ? <span className="h-1.5 w-1.5 rounded-full bg-[#22C55E]" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

const STATE_CHIP_TONES = Object.freeze({
  busy: { chip: "border-sm-accent/40 bg-sm-accent/10 text-sm-primary", dot: "bg-sm-accent node-pulse" },
  ok: { chip: "border-[#22C55E]/30 bg-[#22C55E]/10 text-[#16A34A]", dot: "bg-[#22C55E]" },
  warn: { chip: "border-[#F59E0B]/35 bg-[#F59E0B]/10 text-[#B45309]", dot: "bg-[#F59E0B]" },
  bad: { chip: "border-[#EF4343]/30 bg-[#EF4343]/10 text-[#B91C1C]", dot: "bg-[#EF4343]" },
  quiet: { chip: "border-sm-ink/15 bg-white text-sm-ink/70", dot: "bg-sm-ink/30" },
});

/** The state beside a page's title: a dot and a few words. */
export function StateChip({ tone = "quiet", children, testId }) {
  const styles = STATE_CHIP_TONES[tone] || STATE_CHIP_TONES.quiet;
  return (
    <span data-testid={testId} className={`inline-flex h-8 items-center gap-2 rounded-full border pl-3 pr-3.5 text-[13px] font-bold ${styles.chip}`}>
      <span className={`h-2 w-2 rounded-full ${styles.dot}`} aria-hidden="true" />
      {children}
    </span>
  );
}

const CHIP_TONES = Object.freeze({
  busy: "border-sm-accent/40 bg-sm-accent/10 text-sm-primary",
  ok: "border-[#22C55E]/30 bg-[#22C55E]/10 text-[#16A34A]",
  warn: "border-[#F59E0B]/35 bg-[#F59E0B]/10 text-[#B45309]",
  bad: "border-[#EF4343]/30 bg-[#EF4343]/10 text-[#B91C1C]",
  quiet: "border-sm-ink/15 bg-white text-sm-ink/70",
  navy: "border-sm-ink bg-sm-ink text-white",
});

/** The small chip on a row or a card: a dot and a word or two. `StateChip` is its page-level, 32px sibling. */
export function Chip({ tone = "quiet", dot = true, children, className = "", testId }) {
  return (
    <span data-testid={testId} className={`inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-[11px] font-bold leading-none ${CHIP_TONES[tone] || CHIP_TONES.quiet} ${className}`}>
      {dot ? <span className="h-[7px] w-[7px] rounded-full bg-current opacity-90" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

const ICON_SQUARE_TONES = Object.freeze({
  quiet: "bg-sm-ink/5 text-sm-ink/80",
  ok: "bg-[#22C55E]/10 text-[#16A34A]",
  warn: "bg-[#F59E0B]/[0.12] text-[#B45309]",
  bad: "bg-[#EF4343]/10 text-[#B91C1C]",
});

/**
 * A glossy tile in one colour with a white line icon on it: a guide's category, a section's mark. The gloss and the
 * soft coloured shadow are what make it read as a made object rather than a coloured box.
 */
export function GlossTile({ color, icon: Icon, size = 40, iconSize }) {
  return (
    <span
      className="relative inline-flex shrink-0 items-center justify-center overflow-hidden text-white"
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.3),
        background: `linear-gradient(145deg, color-mix(in srgb, ${color} 72%, #fff), ${color})`,
        boxShadow: `inset 0 0 0 0.5px rgba(255,255,255,0.35), 0 8px 16px -8px color-mix(in srgb, ${color} 70%, transparent)`,
      }}
      aria-hidden="true"
    >
      <span className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.42), rgba(255,255,255,0) 55%)" }} />
      <Icon size={iconSize ?? Math.round(size * 0.5)} strokeWidth={2.1} className="relative" />
    </span>
  );
}

/** A white tile with a soft edge for a brand's own logo, so every logo sits on the same ground. */
export function BrandTile({ size = 44, children }) {
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white"
      style={{ width: size, height: size, boxShadow: "0 1px 2px rgba(15,36,66,0.08), 0 0 0 1px rgba(15,36,66,0.07)" }}
      aria-hidden="true"
    >
      {children}
    </span>
  );
}

/** The 36px rounded square that carries a row's or a panel's icon. */
export function IconSquare({ icon: Icon, tone = "quiet", size = 16 }) {
  return (
    <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] ${ICON_SQUARE_TONES[tone] || ICON_SQUARE_TONES.quiet}`}>
      <Icon size={size} aria-hidden="true" />
    </span>
  );
}
